import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

interface BackButtonProps {
  /** Extra spacing for the screen it sits on. */
  className?: string;
}

/**
 * Takes the user one step back through the auth flow.
 *
 * It walks history rather than naming a route, because the screens it sits on
 * are reached from more than one place - the OTP screen follows the log-in
 * screen on one flow and the consent screen on the other, and only history
 * knows which. Going back that way also restores the router state the previous
 * screen was rendered with, which a fresh navigation would drop.
 */
const BackButton: React.FC<BackButtonProps> = ({ className = "" }) => {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      onClick={() => navigate(-1)}
      // Named for the reader either way: on a phone the word is not drawn, so
      // the label has to come from here instead of from the text node.
      aria-label="Back"
      // On a phone this is the chrome a phone app puts at the top left of a
      // pushed screen: a round 40px button with an arrow in it and no word.
      // The word is there on a desktop because the control is 14px of text in
      // a wide column and needs to say what it is; on a phone it is the only
      // thing in the top left of the screen, which is the one place every app
      // puts the way back - and a circle is what a thumb finds, where a
      // two-word link with an invisible skirt around it is a guess.
      //
      // From sm up every phone class is undone and the `after:` skirt is the
      // hit area again: it is drawn rather than padded because padding would
      // move the label, and every screen this sits on aligns it with the
      // heading underneath.
      className={`relative inline-flex items-center justify-center gap-1.5 h-10 w-10 rounded-full border border-[#E3E7EA] bg-white/80 text-[#1E1F24] shadow-[0_1px_2px_rgba(30,31,36,0.04)] backdrop-blur-sm transition-colors sm:h-auto sm:w-auto sm:rounded-none sm:border-0 sm:bg-transparent sm:shadow-none sm:backdrop-blur-none sm:text-[14px] sm:text-[#62636C] sm:hover:text-[#1E1F24] sm:after:absolute sm:after:-inset-2 sm:after:content-[''] ${className}`}
    >
      <ArrowLeft className="w-[18px] h-[18px] sm:w-4 sm:h-4" aria-hidden />
      <span className="hidden sm:inline">Back</span>
    </button>
  );
};

export default BackButton;
